
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Shield, 
  Key, 
  Lock, 
  AlertTriangle, 
  Eye, 
  EyeOff,
  ArrowLeft,
  Save,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { internalAuthService } from '@/services/internalAuthService';

interface SecuritySettingsProps {
  onBack: () => void;
}

interface SecurityLog {
  id: string;
  user_id: string;
  incident_type: string;
  description: string;
  ip_address?: string;
  resolved: boolean;
  created_at: string;
  resolved_at?: string;
  resolved_by?: string;
}

const SecuritySettings: React.FC<SecuritySettingsProps> = ({ onBack }) => {
  const { user } = useInternalAuth();
  const [loading, setLoading] = useState(false);
  const [showCurrentPin, setShowCurrentPin] = useState(false);
  const [showNewPin, setShowNewPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);
  const [securityLogs, setSecurityLogs] = useState<SecurityLog[]>([]);
  
  // Form states
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [seedPhrase, setSeedPhrase] = useState('');

  useEffect(() => {
    loadSecurityLogs();
  }, []);

  const loadSecurityLogs = async () => {
    try {
      // This would load actual security logs from the database
      // For now, we'll show placeholder data
      const mockLogs: SecurityLog[] = [
        {
          id: '1',
          user_id: user?.id || '',
          incident_type: 'Failed Login Attempt',
          description: 'Multiple failed login attempts detected',
          ip_address: '192.168.1.100',
          resolved: true,
          created_at: new Date(Date.now() - 86400000).toISOString(),
          resolved_at: new Date(Date.now() - 3600000).toISOString(),
          resolved_by: 'System'
        },
        {
          id: '2',
          user_id: user?.id || '',
          incident_type: 'Suspicious Activity',
          description: 'Unusual access pattern detected',
          ip_address: '10.0.0.50',
          resolved: false,
          created_at: new Date(Date.now() - 172800000).toISOString()
        }
      ];
      
      setSecurityLogs(mockLogs);
    } catch (error) {
      console.error('Error loading security logs:', error);
      toast.error('Failed to load security logs');
    }
  };

  const handlePinChange = async () => {
    if (!currentPin || !newPin || !confirmPin) {
      toast.error('Please fill in all PIN fields');
      return;
    }

    if (newPin !== confirmPin) {
      toast.error('New PIN and confirmation do not match');
      return;
    }

    if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
      toast.error('PIN must be exactly 4 digits');
      return;
    }

    if (!user) {
      toast.error('No user logged in');
      return;
    }

    setLoading(true);
    try {
      const result = await internalAuthService.updatePin(
        user.id,
        currentPin,
        newPin
      );

      if (result.success) {
        toast.success('PIN changed successfully');
        setCurrentPin('');
        setNewPin('');
        setConfirmPin('');
        setSeedPhrase('');
      } else {
        toast.error(result.error || 'Failed to change PIN');
      }
    } catch (error) {
      console.error('Error changing PIN:', error);
      toast.error('Failed to change PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateNewSeedPhrase = async () => {
    if (!confirm('Generating a new seed phrase will invalidate your current one. Are you sure?')) {
      return;
    }

    setLoading(true);
    try {
      // Generate a simple seed phrase (12 words)
      const words = [
        'apple', 'banana', 'cherry', 'date', 'elderberry', 'fig', 
        'grape', 'honeydew', 'kiwi', 'lemon', 'mango', 'orange',
        'peach', 'quince', 'raspberry', 'strawberry', 'tangerine', 'watermelon'
      ];
      
      const seedWords = [];
      for (let i = 0; i < 12; i++) {
        const randomIndex = Math.floor(Math.random() * words.length);
        seedWords.push(words[randomIndex]);
      }
      
      const newSeedPhrase = seedWords.join(' ');
      
      // Show the new seed phrase to the user
      alert(`Your new seed phrase is:\n\n${newSeedPhrase}\n\nPlease save this securely. This message will only be shown once.`);
      
      toast.success('New seed phrase generated. Please save it securely.');
    } catch (error) {
      console.error('Error generating seed phrase:', error);
      toast.error('Failed to generate new seed phrase');
    } finally {
      setLoading(false);
    }
  };

  const getIncidentTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'failed login attempt': return 'bg-yellow-500';
      case 'suspicious activity': return 'bg-red-500';
      case 'unauthorized access': return 'bg-red-600';
      case 'password breach': return 'bg-red-700';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          onClick={onBack}
          variant="outline"
          className="border-gray-600 text-gray-300 hover:bg-gray-700"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin Panel
        </Button>
        <h1 className="text-2xl font-bold text-white">Security Settings</h1>
      </div>

      {/* Account Security */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="h-5 w-5 text-red-400" />
            Account Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="username" className="text-gray-300">Username</Label>
              <Input
                id="username"
                value={user?.username || ''}
                disabled
                className="bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed"
              />
            </div>
            <div>
              <Label htmlFor="last-login" className="text-gray-300">Last Login</Label>
              <Input
                id="last-login"
                value={user?.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                disabled
                className="bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PIN Management */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Key className="h-5 w-5 text-blue-400" />
            PIN Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="current-pin" className="text-gray-300">Current PIN</Label>
              <div className="relative">
                <Input
                  id="current-pin"
                  type={showCurrentPin ? 'text' : 'password'}
                  value={currentPin}
                  onChange={(e) => setCurrentPin(e.target.value)}
                  placeholder="Enter current PIN"
                  maxLength={4}
                  className="bg-gray-700 border-gray-600 text-white pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 text-gray-400 hover:text-white"
                  onClick={() => setShowCurrentPin(!showCurrentPin)}
                >
                  {showCurrentPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="new-pin" className="text-gray-300">New PIN</Label>
              <div className="relative">
                <Input
                  id="new-pin"
                  type={showNewPin ? 'text' : 'password'}
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value)}
                  placeholder="Enter new PIN"
                  maxLength={4}
                  className="bg-gray-700 border-gray-600 text-white pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 text-gray-400 hover:text-white"
                  onClick={() => setShowNewPin(!showNewPin)}
                >
                  {showNewPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="confirm-pin" className="text-gray-300">Confirm New PIN</Label>
              <div className="relative">
                <Input
                  id="confirm-pin"
                  type={showConfirmPin ? 'text' : 'password'}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value)}
                  placeholder="Confirm new PIN"
                  maxLength={4}
                  className="bg-gray-700 border-gray-600 text-white pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 text-gray-400 hover:text-white"
                  onClick={() => setShowConfirmPin(!showConfirmPin)}
                >
                  {showConfirmPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={handlePinChange}
              disabled={loading}
              className="bg-sports-green text-black hover:bg-green-600"
            >
              <Save className="h-4 w-4 mr-2" />
              Change PIN
            </Button>
            <Button
              onClick={handleGenerateNewSeedPhrase}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Generate New Seed Phrase
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Security Logs */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
            Recent Security Events
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-700">
                  <TableHead className="text-gray-300">Type</TableHead>
                  <TableHead className="text-gray-300">Description</TableHead>
                  <TableHead className="text-gray-300">IP Address</TableHead>
                  <TableHead className="text-gray-300">Status</TableHead>
                  <TableHead className="text-gray-300">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {securityLogs.map((log) => (
                  <TableRow key={log.id} className="border-gray-700">
                    <TableCell>
                      <Badge className={`${getIncidentTypeColor(log.incident_type)} text-white`}>
                        {log.incident_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-300">{log.description}</TableCell>
                    <TableCell className="text-gray-300 font-mono text-sm">
                      {log.ip_address || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Badge className={log.resolved ? 'bg-green-500' : 'bg-red-500'}>
                        {log.resolved ? 'Resolved' : 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-300">
                      {new Date(log.created_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecuritySettings;
